import {
  decodePage,
  isRecord,
  json,
  nullableString,
  request,
  requireArray,
  requireBoolean,
  requireNumber,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'

type Schemas = components['schemas']
export const PRINTER_EVENT_RULE_POLICY_TYPE = 'printer:event-rule'
export type EventRuleRecipient = Schemas['EventRuleRecipient']
export type EventRuleAction = Schemas['EventRuleAction']
export type EventRuleContent = Omit<
  Schemas['EventRuleContent'],
  'configuration'
> & { configuration: unknown }
export type PrinterEventRulePolicy = Omit<
  Schemas['PrinterEventRuleDetail'],
  'rule'
> & {
  rule: Omit<Schemas['EventRuleVersion'], 'content'> & {
    content: EventRuleContent
  }
}
export type EventRulePolicyEdit = Omit<
  Schemas['CreatePrinterEventRuleRequest'],
  'operationId' | 'ruleId' | 'content' | 'selectorExpression'
> & { content: EventRuleContent; selectorExpression: string | null }
export type EventRulePolicyMutation =
  Schemas['PrinterEventRuleMutationResponse']
export type EventRuleWorkflow = Schemas['PrinterEventRuleWorkflow']
export type EventRuleWorkflowOption = EventRuleWorkflow & {
  description: string | null
}

function decodeWorkflow(raw: unknown): EventRuleWorkflow {
  if (!isRecord(raw)) throw new Error('Malformed linked workflow')
  return {
    definitionId: requireString(raw.definitionId, 'workflow definition id'),
    name: requireString(raw.name, 'workflow name'),
    ownerEntityId: nullableString(raw.ownerEntityId, 'workflow owner'),
    key: nullableString(raw.key, 'workflow key'),
  } satisfies EventRuleWorkflow
}

function decodeWorkflowOption(raw: unknown): EventRuleWorkflowOption {
  if (!isRecord(raw)) throw new Error('Malformed workflow')
  return {
    ...decodeWorkflow(raw),
    description: nullableString(raw.description, 'workflow description'),
  }
}

export function listEventRuleWorkflowOptions(
  entityId: string,
  signal?: AbortSignal,
) {
  return request(
    `/api/v1/entities/${entityId}/workflows`,
    (raw) => {
      if (!isRecord(raw)) throw new Error('Malformed workflow list')
      return requireArray(raw.items, 'workflows', decodeWorkflowOption)
    },
    { signal },
  )
}

function decodeRecipient(raw: unknown): EventRuleRecipient {
  if (!isRecord(raw)) throw new Error('Malformed notification recipient')
  return {
    type: requireString(raw.type, 'recipient type'),
    id: nullableString(raw.id, 'recipient id'),
    address: nullableString(raw.address, 'recipient address'),
  } satisfies EventRuleRecipient
}

function decodeContent(raw: unknown): EventRuleContent {
  if (!isRecord(raw) || !isRecord(raw.configuration))
    throw new Error('Malformed event rule content')
  return {
    detectorType: requireString(raw.detectorType, 'detector type'),
    configuration: raw.configuration,
    actions: requireArray(raw.actions, 'notification actions', (action) => {
      if (!isRecord(action)) throw new Error('Malformed notification action')
      return {
        type: requireString(action.type, 'action type'),
        recipients: requireArray(
          action.recipients,
          'recipients',
          decodeRecipient,
        ),
      } satisfies EventRuleAction
    }),
  } satisfies EventRuleContent
}

function decodeReference(raw: unknown) {
  if (!isRecord(raw)) throw new Error('Malformed event rule reference')
  return {
    ruleId: requireString(raw.ruleId, 'rule id'),
    ownerEntityId: requireString(raw.ownerEntityId, 'rule owner'),
    version: requireNumber(raw.version, 'rule version'),
    payloadDigest: nullableString(raw.payloadDigest, 'rule digest'),
  } satisfies Schemas['EventRuleReference']
}

function decodePolicy(raw: unknown): PrinterEventRulePolicy {
  if (!isRecord(raw) || !isRecord(raw.rule))
    throw new Error('Malformed printer event rule policy')
  return {
    policyId: requireString(raw.policyId, 'policy id'),
    name: requireString(raw.name, 'policy name'),
    policyRevision: requireNumber(raw.policyRevision, 'policy revision'),
    selectorExpression: nullableString(
      raw.selectorExpression,
      'policy selector',
    ),
    selectorGui: raw.selectorGui ?? null,
    priority: requireNumber(raw.priority, 'policy position'),
    overridesPolicyId: nullableString(
      raw.overridesPolicyId,
      'overridden policy',
    ),
    rule: {
      reference: decodeReference(raw.rule.reference),
      content: decodeContent(raw.rule.content),
    },
    actionsRedacted: requireBoolean(raw.actionsRedacted, 'actions redacted'),
    workflows:
      raw.workflows === undefined
        ? []
        : requireArray(raw.workflows, 'linked workflows', decodeWorkflow),
  } satisfies PrinterEventRulePolicy
}

function decodeMutation(raw: unknown): EventRulePolicyMutation {
  if (!isRecord(raw)) throw new Error('Malformed event rule save response')
  return {
    policyId: requireString(raw.policyId, 'policy id'),
    policyRevision: requireNumber(raw.policyRevision, 'policy revision'),
    changeId: nullableString(raw.changeId, 'change id'),
    rule: raw.rule === null ? null : decodeReference(raw.rule),
    deleted: requireBoolean(raw.deleted, 'deleted'),
  } satisfies EventRulePolicyMutation
}

const path = (entityId: string, policyId?: string) =>
  `/api/v1/entities/${entityId}/printer-event-rule-policies${policyId ? `/${policyId}` : ''}`
export const eventRulePoliciesApi = {
  get: (entityId: string, policyId: string, signal?: AbortSignal) =>
    request(path(entityId, policyId), decodePolicy, { signal }),
  list: (entityId: string, after?: string, signal?: AbortSignal) => {
    const query = new URLSearchParams({ limit: '100' })
    if (after) query.set('after', after)
    return request(
      `${path(entityId)}?${query}`,
      (raw) => decodePage(raw, decodePolicy, 'printer event rule policies'),
      { signal },
    )
  },
  create: (
    entityId: string,
    operationId: string,
    ruleId: string,
    edit: EventRulePolicyEdit,
  ) =>
    request(
      path(entityId),
      decodeMutation,
      json('POST', { operationId, ruleId, ...edit }),
    ),
  update: (
    entityId: string,
    policy: PrinterEventRulePolicy,
    edit: EventRulePolicyEdit,
  ) =>
    request(
      path(entityId, policy.policyId),
      decodeMutation,
      json('PUT', { expectedRevision: policy.policyRevision, ...edit }),
    ),
  delete: (entityId: string, policy: PrinterEventRulePolicy) =>
    request(path(entityId, policy.policyId), decodeMutation, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expectedRevision: policy.policyRevision }),
    }),
}
